import { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../contexts/AuthProvider";
import {
  fetchDraftBookings,
  fetchPendingAddOns,
  type DraftBooking,
} from "../../services/BookingService";
import {
  getPendingPackagePurchases,
  type PendingPackagePurchase,
} from "../../services/PackageService";
import { getCachedBookings } from "../../utils/bookingCache";
import { getCachedPackages } from "../../utils/packageCache";

const TOAST_DURATION = 15;
const CART_TOAST_DURATION = 10;

function uniqueDrafts(drafts: DraftBooking[]) {
  return Array.from(
    drafts
      .reduce((map, draft) => {
        if (!map.has(draft.id)) map.set(draft.id, draft);
        return map;
      }, new Map<number, DraftBooking>())
      .values(),
  );
}

function uniquePendingPackages(packages: PendingPackagePurchase[]) {
  return Array.from(
    packages
      .reduce((map, pkg) => {
        if (!map.has(pkg.id)) map.set(pkg.id, pkg);
        return map;
      }, new Map<number, PendingPackagePurchase>())
      .values(),
  );
}

export default function useDraftBookingNotifications() {
  const { role, loading: authLoading, user } = useAuth();
  const [drafts, setDrafts] = useState<DraftBooking[]>([]);
  const [pendingPackages, setPendingPackages] = useState<PendingPackagePurchase[]>([]);
  const [addOnSessionCount, setAddOnSessionCount] = useState(0);
  const [whatsAppFailureCount, setWhatsAppFailureCount] = useState(0);
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = user?.id != null ? String(user.id) : null;
  const [remainingSecondsByDraft, setRemainingSecondsByDraft] = useState(
    () => new Map<number, number>(),
  );
  const [remainingSecondsByPackage, setRemainingSecondsByPackage] = useState(
    () => new Map<number, number>(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOnError, setAddOnError] = useState<string | null>(null);
  const [packageError, setPackageError] = useState<string | null>(null);

  // Toast state lives here so it survives page navigation within the same layout
  const [toastVisible, setToastVisible] = useState(false);
  const [toastTimeLeft, setToastTimeLeft] = useState(TOAST_DURATION);
  const prevAddOnCountRef = useRef<number | null>(null);

  const [phoneWarningToastVisible, setPhoneWarningToastVisible] = useState(false);
  const phoneWarningShownRef = useRef(false);

  const [cartToastVisible, setCartToastVisible] = useState(false);
  const [cartToastTimeLeft, setCartToastTimeLeft] = useState(CART_TOAST_DURATION);

  async function loadDrafts(silent = false) {
    if (!silent) setLoading(true);
    const [draftResult, addOnResult, packageResult] = await Promise.all([
      fetchDraftBookings(),
      fetchPendingAddOns(),
      getPendingPackagePurchases(),
    ]);
    if (!silent) setLoading(false);
    setError(draftResult.error);
    if (!draftResult.error) {
      setDrafts(draftResult.drafts);
    }
    setAddOnError(addOnResult.error);
    if (!addOnResult.error && addOnResult.data !== null) {
      setAddOnSessionCount(addOnResult.data.length);
    }
    setPackageError(packageResult.error);
    if (!packageResult.error && packageResult.data !== null) {
      setPendingPackages(packageResult.data);
    }
    const uid = userIdRef.current;
    if (uid) {
      const [bookingResult, packageResult] = await Promise.all([
        getCachedBookings(uid),
        getCachedPackages(uid),
      ]);
      const bookingFailCount = bookingResult.bookings.filter(
        (b) =>
          b.whatsapp_confirmation_status === "failed" ||
          b.whatsapp_confirmation_status === "undelivered",
      ).length;
      const packageFailCount = packageResult.packages.filter(
        (p) =>
          p.whatsapp_confirmation_status === "failed" ||
          p.whatsapp_confirmation_status === "undelivered",
      ).length;
      setWhatsAppFailureCount(bookingFailCount + packageFailCount);
    }
  }

  useEffect(() => {
    if (authLoading || role !== "homeowner") {
      setDrafts([]);
      setPendingPackages([]);
      setAddOnSessionCount(0);
      setWhatsAppFailureCount(0);
      setError(null);
      setAddOnError(null);
      setPackageError(null);
      prevAddOnCountRef.current = null;
      return;
    }

    void loadDrafts();

    // Poll every 30 s so the badge reflects new add-ons submitted by the technician
    const interval = window.setInterval(() => void loadDrafts(true), 30_000);

    // Refresh immediately when the user returns to the tab
    const onVisible = () => { if (document.visibilityState === "visible") void loadDrafts(true); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [role, authLoading]);

  useEffect(() => {
    setRemainingSecondsByDraft(
      new Map(
        drafts.map((draft) => [
          draft.id,
          Math.max(0, Math.floor(draft.expires_in_seconds)),
        ]),
      ),
    );
  }, [drafts]);

  useEffect(() => {
    setRemainingSecondsByPackage(
      new Map(
        pendingPackages.map((pkg) => [
          pkg.id,
          Math.max(0, Math.floor(pkg.expires_in_seconds)),
        ]),
      ),
    );
  }, [pendingPackages]);

  useEffect(() => {
    if (drafts.length === 0 && pendingPackages.length === 0) return undefined;

    const intervalId = window.setInterval(() => {
      setRemainingSecondsByDraft((current) => {
        const next = new Map(current);
        next.forEach((remainingSeconds, draftId) => {
          next.set(draftId, Math.max(0, remainingSeconds - 1));
        });
        return next;
      });
      setRemainingSecondsByPackage((current) => {
        const next = new Map(current);
        next.forEach((remainingSeconds, packageId) => {
          next.set(packageId, Math.max(0, remainingSeconds - 1));
        });
        return next;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [drafts.length, pendingPackages.length]);

  // Show toast when addOnSessionCount increases after the initial load
  useEffect(() => {
    if (prevAddOnCountRef.current === null) {
      prevAddOnCountRef.current = addOnSessionCount;
      return;
    }
    if (addOnSessionCount > prevAddOnCountRef.current) {
      setToastTimeLeft(TOAST_DURATION);
      setToastVisible(true);
    }
    prevAddOnCountRef.current = addOnSessionCount;
  }, [addOnSessionCount]);

  // Countdown tick
  useEffect(() => {
    if (!toastVisible) return;
    if (toastTimeLeft <= 0) {
      setToastVisible(false);
      return;
    }
    const id = window.setTimeout(() => setToastTimeLeft((t) => t - 1), 1000);
    return () => window.clearTimeout(id);
  }, [toastVisible, toastTimeLeft]);

  // Countdown tick for cart toast
  useEffect(() => {
    if (!cartToastVisible) return;
    if (cartToastTimeLeft <= 0) {
      setCartToastVisible(false);
      return;
    }
    const id = window.setTimeout(() => setCartToastTimeLeft((t) => t - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cartToastVisible, cartToastTimeLeft]);

  // Show phone warning toast once per session when whatsAppFailureCount > 0
  useEffect(() => {
    if (whatsAppFailureCount > 0 && !phoneWarningShownRef.current) {
      phoneWarningShownRef.current = true;
      setPhoneWarningToastVisible(true);
    }
    if (whatsAppFailureCount === 0) {
      setPhoneWarningToastVisible(false);
      phoneWarningShownRef.current = false;
    }
  }, [whatsAppFailureCount]);

  const visibleDrafts = useMemo(
    () =>
      uniqueDrafts(drafts)
        .map((draft) => ({
          ...draft,
          expires_in_seconds:
            remainingSecondsByDraft.get(draft.id) ?? draft.expires_in_seconds,
        }))
        .filter((draft) => draft.expires_in_seconds > 0),
    [drafts, remainingSecondsByDraft],
  );
  const visiblePendingPackages = useMemo(
    () =>
      uniquePendingPackages(pendingPackages)
        .map((pkg) => ({
          ...pkg,
          expires_in_seconds:
            remainingSecondsByPackage.get(pkg.id) ?? pkg.expires_in_seconds,
        }))
        .filter((pkg) => pkg.expires_in_seconds > 0),
    [pendingPackages, remainingSecondsByPackage],
  );
  const pendingPackageCount = visiblePendingPackages.length;
  const count = visibleDrafts.length + addOnSessionCount + pendingPackageCount;

  return {
    drafts,
    visibleDrafts,
    pendingPackages,
    visiblePendingPackages,
    count,
    addOnSessionCount,
    pendingPackageCount,
    whatsAppFailureCount,
    loading,
    error: error ?? addOnError ?? packageError,
    draftError: error,
    addOnError,
    packageError,
    refresh: loadDrafts,
    toast: {
      visible: toastVisible,
      timeLeft: toastTimeLeft,
      dismiss: () => setToastVisible(false),
    },
    phoneWarningToast: {
      visible: phoneWarningToastVisible,
      dismiss: () => setPhoneWarningToastVisible(false),
    },
    pendingCartToast: {
      visible: cartToastVisible,
      timeLeft: cartToastTimeLeft,
      dismiss: () => setCartToastVisible(false),
      show: () => {
        setCartToastTimeLeft(CART_TOAST_DURATION);
        setCartToastVisible(true);
      },
    },
  };
}

export type DraftBookingNotifications = ReturnType<
  typeof useDraftBookingNotifications
>;
